import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import Card from '../../components/common/Card';
import {type TypedTranslation} from '../../../i18n';
import {createLabelPreset, deleteLabelPreset, fetchLabelPresets, updateLabelPreset} from '../../../service/presentationLabelPreset.service';
import {IPresentationLabelPreset} from '../../../incase-domain/presentationLabelPreset';
import {PlayerLabel, DEFAULT_PLAYER_LABELS, LOCALE_LABEL_ITEMS} from '../../../incase-domain/playerLabel';
import Button from '../../../incase-domain/components/button/Button';
import DataView, {ViewMode} from '../../components/common/data/DataView';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import InputWithCharacterCounter from '../../../incase-domain/components/input/InputWithCharacterCounter';

const MAX_PRESET_NAME_LENGTH = 64;

function initLabels(saved?: PlayerLabel[]): PlayerLabel[] {
    return DEFAULT_PLAYER_LABELS.map((def) => {
        const override = (saved ?? []).find((l) => l.code === def.code);
        return override ? {...override} : ({...def, _id: def.code} as PlayerLabel);
    });
}

function PresentationLabelPresets(): JSX.Element {
    const {t} = useTranslation();
    const queryClient = useQueryClient();

    const {data: presets, isFetching, isLoading} = useQuery(QUERY_KEYS.labelPreset.lists(), () => fetchLabelPresets());

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedForDeletion, setSelectedForDeletion] = useState<IPresentationLabelPreset>({} as IPresentationLabelPreset);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingPreset, setEditingPreset] = useState<IPresentationLabelPreset | null>(null);
    const [modalName, setModalName] = useState('');
    const [modalLabels, setModalLabels] = useState<PlayerLabel[]>([]);

    const invalidate = (): void => {
        queryClient.invalidateQueries({queryKey: QUERY_KEYS.labelPreset.lists()});
    };

    const {mutate: handleCreate} = useMutation(() => createLabelPreset(modalName, modalLabels), {
        mutationKey: ['create_label_preset'],
        onSuccess: () => {
            invalidate();
            closeEditModal();
        },
        onError: handleError,
    });

    const {mutate: handleUpdate} = useMutation(
        () => updateLabelPreset(editingPreset?._id as string, {name: modalName, labels: modalLabels}),
        {
            mutationKey: ['update_label_preset'],
            onSuccess: () => {
                invalidate();
                closeEditModal();
            },
            onError: handleError,
        }
    );

    const {mutate: handleDelete} = useMutation((id: string) => deleteLabelPreset(id), {
        mutationKey: ['delete_label_preset'],
        onSuccess: () => invalidate(),
        onError: handleError,
    });

    function handleError(error: unknown): void {
        if (axios.isAxiosError(error)) {
            const data = (error as AxiosError<{status: number; message: string}>).response?.data;
            if (data) {
                toast.error(t(data.message as TypedTranslation));
            }
        }
    }

    function openCreateModal(): void {
        setEditingPreset(null);
        setModalName('');
        setModalLabels(initLabels([]));
        setEditModalOpen(true);
    }

    function openEditModal(preset: IPresentationLabelPreset): void {
        setEditingPreset(preset);
        setModalName(preset.name);
        setModalLabels(initLabels(preset.labels));
        setEditModalOpen(true);
    }

    function closeEditModal(): void {
        setEditModalOpen(false);
        setEditingPreset(null);
        setModalName('');
        setModalLabels([]);
    }

    function onSaveConfirm(): void {
        const trimmed = modalName.trim();
        if (!trimmed) {
            toast.error(t('labelPreset.nameRequired'));
            return;
        }
        if (editingPreset) {
            handleUpdate();
        } else {
            handleCreate();
        }
    }

    function handleLabelChange(code: string, value: string): void {
        setModalLabels((prev) => prev.map((l) => (l.code === code ? {...l, en: value, hr: value} : l)));
    }

    const renderName = (preset: IPresentationLabelPreset): JSX.Element => <>{preset.name}</>;

    const renderActions = (preset: IPresentationLabelPreset): JSX.Element => (
        <div className="u-flex u-gap-1">
            <Button
                leading="solar:pen-linear"
                variant="ghost"
                color="secondary"
                square
                size="sm"
                onClick={(e) => {
                    e?.stopPropagation();
                    openEditModal(preset);
                }}
            />
            <Button
                leading="solar:trash-bin-trash-linear"
                variant="ghost"
                color="error"
                square
                size="sm"
                onClick={(e) => {
                    e?.stopPropagation();
                    setSelectedForDeletion(preset);
                    setDeleteModalOpen(true);
                }}
            />
        </div>
    );

    return (
        <div className="c-label-presets">
            <Card>
                <DataView
                    views={[
                        {
                            columns: [
                                {
                                    label: t('labelPreset.name'),
                                    key: 'name',
                                    body: (row) => renderName(row),
                                },
                                {
                                    body: (row) => renderActions(row),
                                    width: '8rem',
                                },
                            ],
                            view: ViewMode.TABLE,
                        },
                    ]}
                    rows={presets || []}
                    rowIdentifier={(row) => row._id}
                    onRowClick={(row) => openEditModal(row)}
                    loading={isFetching || isLoading}
                    title={t('labelPreset.header')}
                    headerActionPrimary={{
                        icon: 'ph:plus-bold',
                        onClick: openCreateModal,
                        title: t('genericTable.new'),
                    }}
                    inlineTitle
                />
            </Card>

            <ConfirmModal
                title={editingPreset ? t('labelPreset.edit') : t('labelPreset.create')}
                icon={editingPreset ? 'solar:pen-linear' : 'ph:plus-bold'}
                isOpen={editModalOpen}
                showConfirm
                confirmText={t('common.save')}
                onConfirm={onSaveConfirm}
                showCancel
                onCancel={closeEditModal}
            >
                <div className="u-flex u-flex-col u-gap-3">
                    <InputWithCharacterCounter
                        value={modalName}
                        onChange={setModalName}
                        placeholder={t('labelPreset.namePlaceholder')}
                        maxLength={MAX_PRESET_NAME_LENGTH}
                        inlineMaxLength
                        onKeyUp={(e) => {
                            if (e.key === 'Enter') onSaveConfirm();
                        }}
                    />

                    <div className="u-flex u-flex-col u-gap-1">
                        <p className="u-text-sm u-mb-1" style={{color: 'var(--color-text-secondary)'}}>
                            {t('labelPreset.labelsDescription')}
                        </p>
                        {LOCALE_LABEL_ITEMS.map(({code, nameKey}) => {
                            const label = modalLabels.find((l) => l.code === code);
                            if (!label) return null;
                            return (
                                <div key={code} className="c-settingsItem">
                                    <div className="u-flex u-h-between u-v-center u-gap-4">
                                        <p className="c-settingsItem__title">{t(nameKey as never)}</p>
                                        <div className="c-sidepanel__input" style={{flex: '0 0 180px'}}>
                                            <input
                                                type="text"
                                                value={label.en}
                                                onChange={(e) => handleLabelChange(code, e.target.value)}
                                                aria-label={t(nameKey as never)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </ConfirmModal>

            <ConfirmModal
                message={t('labelPreset.confirmDelete', {presetName: selectedForDeletion.name})}
                color="error"
                isOpen={deleteModalOpen}
                showConfirm
                confirmText={t('common.delete')}
                onConfirm={() => {
                    handleDelete(selectedForDeletion._id);
                    setDeleteModalOpen(false);
                }}
                showCancel
                onCancel={() => setDeleteModalOpen(false)}
            />
        </div>
    );
}

export default PresentationLabelPresets;
